/**
 * The branch page's follow-up of its Application ITSELF while a deployment
 * settles (BranchPage.tsx) — a fresh environment handed over by
 * BranchPreparing, a push being rolled out on a permanent branch.
 *
 * Everything else the page re-reads quietly hangs on a change of the
 * Application's change key (`podsFollowUp.ts`): the pods, the « Ressources »,
 * « À surveiller » and code cards, the pipeline, the init report, the sync
 * watch. That key only moves when the Application is read again — a list
 * read elsewhere, the sync watch while a deployment fails. A reader staying
 * on the branch page reads no list: without this follow-up a deployment
 * watched from there never moves the key, and nothing follows it.
 *
 *  - EVERY `SETTLING_POLL_MS` WHILE THE APPLICATION IS NOT SETTLED
 *    (`isApplicationSettled`, of the newest state known): one GET of the
 *    Application, offered to the page's newest state known — what moves
 *    the key, and the band, the deployments and the banner with it.
 *    Paused while the tab is hidden (`useVisiblePolling`); a tick is skipped
 *    while another poll already reads the Application (`covered`: the sync
 *    watch).
 *  - BOUNDED: after `SETTLING_POLL_LIMIT` reads in a row that saw the same
 *    change key, the poll stops until the key changes — a list read
 *    elsewhere, the sync watch — or the page loads again (Refresh, another
 *    branch). A key changing to a settled state stops it as well.
 *
 * One read at a time: the page shares it with its other quiet GET of the
 * Application (`createSingleFlight`).
 */

import { useRef, useState } from 'react';
import type { ArgoApplication } from './argoApp';
import { applicationState, isApplicationMoving } from './podsFollowUp';
import { useVisiblePolling } from './visiblePolling';

/** The poll's cadence while the Application settles. */
export const SETTLING_POLL_MS = 10_000;
/**
 * Polled reads in a row without a change of the change key before the poll
 * stops: 90 at `SETTLING_POLL_MS`, fifteen minutes of visible polling.
 */
export const SETTLING_POLL_LIMIT = 90;
/**
 * How long after its last activity an Application that is neither rolling
 * out nor deployed and healthy is still taken for one on its way — the same
 * fifteen minutes as the poll's budget.
 */
export const SETTLING_RECENT_MS = 15 * 60_000;

/** The timestamps of an Application's activity, as ArgoCD writes them. */
function activityTimes(app: ArgoApplication): number[] {
  const history = app.status?.history ?? [];
  const last = history.length > 0 ? history[history.length - 1] : undefined;
  return [
    app.metadata.creationTimestamp,
    app.status?.operationState?.startedAt,
    app.status?.operationState?.finishedAt,
    last?.deployedAt,
  ]
    .map(value => (value ? Date.parse(value) : NaN))
    .filter(time => !Number.isNaN(time));
}

/**
 * Whether the Application's last activity — its creation, its last
 * operation begun or ended, its last deployment — is younger than
 * {@link SETTLING_RECENT_MS}. False without any of these timestamps: an
 * unknown age costs nothing.
 */
export function isApplicationRecent(
  app: ArgoApplication,
  now: number,
): boolean {
  const times = activityTimes(app);
  return times.length > 0 && now - Math.max(...times) < SETTLING_RECENT_MS;
}

/**
 * Whether the Application has reached its rest — the rule of the poll above
 * and of the pods card's (BranchPage.tsx):
 *  - rolling out (`isApplicationMoving`: health `Progressing`, or an
 *    operation `Running`): never settled, whatever its age;
 *  - sync `Synced`, health `Healthy` and at least one deployment in
 *    `status.history`: settled;
 *  - anything else — `OutOfSync`, health `Missing`, `Degraded`, `Unknown`,
 *    never deployed — is a deployment on its way while the Application is
 *    recent ({@link isApplicationRecent}), and its lasting state after:
 *    an environment drifted or broken for days costs no read.
 */
export function isApplicationSettled(
  app: ArgoApplication,
  now: number,
): boolean {
  const state = applicationState(app);
  if (isApplicationMoving(state)) {
    return false;
  }
  if (
    state.sync === 'Synced' &&
    state.health === 'Healthy' &&
    (app.status?.history?.length ?? 0) > 0
  ) {
    return true;
  }
  return !isApplicationRecent(app, now);
}

export interface ApplicationSettlingOptions {
  /**
   * The Application of the page's load — undefined while there is none. A
   * new one (a load, Refresh) starts the follow-up over.
   */
  load: object | undefined;
  /** The newest state known of it: what « settled » is judged on. */
  application: ArgoApplication | undefined;
  /** `applicationChangeKey` of that state. */
  changeKey: string | undefined;
  /** Whether another poll reads the Application already: the tick is skipped. */
  covered?: () => boolean;
  /** The quiet GET of the Application; its failures are its own. */
  read: () => Promise<unknown>;
}

export function useApplicationSettling(
  options: ApplicationSettlingOptions,
): void {
  const { load, application, changeKey } = options;
  const latest = useRef(options);
  latest.current = options;

  // The reads in a row made for the same change key, and the key the poll
  // stopped on.
  const streak = useRef<{ load?: object; key?: string; count: number }>({
    count: 0,
  });
  const [stopped, setStopped] = useState<{ load: object; key: string }>();
  const polling =
    load !== undefined &&
    application !== undefined &&
    changeKey !== undefined &&
    !(stopped?.load === load && stopped.key === changeKey) &&
    !isApplicationSettled(application, Date.now());

  useVisiblePolling(
    () => {
      const current = latest.current;
      const currentLoad = current.load;
      const key = current.changeKey;
      if (
        currentLoad === undefined ||
        current.application === undefined ||
        key === undefined
      ) {
        return undefined;
      }
      // Settled by age alone: no render says so.
      if (isApplicationSettled(current.application, Date.now())) {
        setStopped({ load: currentLoad, key });
        return undefined;
      }
      if (streak.current.load !== currentLoad || streak.current.key !== key) {
        streak.current = { load: currentLoad, key, count: 0 };
      }
      if (streak.current.count >= SETTLING_POLL_LIMIT) {
        setStopped({ load: currentLoad, key });
        return undefined;
      }
      if (current.covered?.()) {
        return undefined;
      }
      streak.current.count += 1;
      return current.read();
    },
    SETTLING_POLL_MS,
    polling,
  );
}
